import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import { SignaturePad } from "../components/SignaturePad";
import DictateButton from "../components/DictateButton";

const TASKS = [
  ["115", "Meal Preparation"], ["116", "Housework/Chores"], ["118", "Managing Medications"],
  ["119", "Shopping"], ["120", "Transportation"], ["122", "Hygiene"], ["123", "Dressing Upper"],
  ["124", "Dressing Lower"], ["125", "Locomotion"], ["126", "Transfer"], ["127", "Toilet Use"],
  ["128", "Bed Mobility"], ["129", "Eating"], ["130", "Bladder Incontinence"],
  ["131", "Bowel Incontinence"], ["132", "Personal Care"], ["134", "Bathing"], ["135", "Bathing (Shower)"],
];

const REASONS = ["App not working", "EVV down", "Forgot to clock in / out", "Visit outside client's home (off-site)", "Other"];

export default function MissedPunchFormPage() {
  const { user } = useAuth();
  useEffect(() => {
    if (user?.name) setForm((f) => (f.employee_signature ? f : { ...f, employee_signature: user.name }));
  }, [user]);
  const navigate = useNavigate();
  const [form, setForm] = useState({ work_date: "", week_ending: "", shift_start: "", shift_end: "", client_name: "", reason_details: "", notes: "", employee_signature: "", client_signer: "" });
  const [reasons, setReasons] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [certified, setCertified] = useState(false);
  const [addr, setAddr] = useState({ street: "", street2: "", city: "", state: "PA", zip: "" });
  const [signerPhone, setSignerPhone] = useState("");
  const [signerDate, setSignerDate] = useState("");
  const [clientSig, setClientSig] = useState(null);
  const [showPolicy, setShowPolicy] = useState(false);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const toggle = (list, setList, val) => setList((l) => (l.includes(val) ? l.filter((x) => x !== val) : [...l, val]));
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async () => {
    setBusy(true);
    try {
      if (!clientSig) toast.warning("No client signature — the office will follow up, but payment may be delayed until it's on file.");
      await api.post("/missed-punch", { ...form, reasons, tasks: tasks.map((t) => t.join(" - ")),
        client_address: addr, client_signer_phone: signerPhone, client_sign_date: signerDate,
        client_signature_png: clientSig || "" });
      setDone(true);
      window.scrollTo(0, 0);
    } catch (e) { toast.error(formatApiError(e)); }
    setBusy(false);
  };

  if (done) {
    return (
      <div className="mx-auto max-w-lg py-16 text-center" data-testid="mp-success">
        <h1 className="font-heading text-2xl font-bold text-stone-900">Missed punch reported</h1>
        <p className="mt-3 text-sm text-stone-600">Your form is in the manager review queue. You'll see the outcome on your profile once reviewed.</p>
        <Button onClick={() => navigate("/my-profile")} className="mt-6 bg-primary text-primary-foreground hover:bg-stone-900" data-testid="mp-done-button">Back to My Profile</Button>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-2xl space-y-5" data-testid="missed-punch-form-page">
      <div>
        <h1 className="font-heading text-3xl font-bold text-stone-900" data-testid="mp-form-title">Missed Punch Form</h1>
        <p className="mt-1 text-sm text-stone-500">Submitting as <span className="font-medium text-stone-700">{user?.name}</span> ({user?.email}){(user?.profile?.employee_id) ? ` · Employee ID ${user.profile.employee_id}` : ""}</p>
      </div>

      <div className="rounded-lg border border-primary/30 bg-primary/5 p-4 text-sm text-stone-700" data-testid="mp-callin-reminder">
        <span className="font-semibold text-primary">If this just happened, call the agency FIRST.</span> A real-time call-in protects your compliance record — missed punches without a call-in count against you.
      </div>

      <div className="rounded-lg border border-stone-200 bg-white p-4 shadow-sm" data-testid="mp-policy">
        <button onClick={() => setShowPolicy((v) => !v)} className="text-sm font-semibold text-stone-800 hover:text-primary" data-testid="mp-policy-toggle">
          {showPolicy ? "Hide" : "Read"} the Missed Punch policy (updated May 1, 2025)
        </button>
        {showPolicy && (
          <div className="mt-3 space-y-2 text-xs leading-relaxed text-stone-600">
            <p>As a Community HealthChoices provider, Silver Home Care is required by law to collect time and attendance data through Electronic Visit Verification (EVV). You must clock in and out using the HHAeXchange mobile app or the HHAeXchange IVR at <b>888-206-5826</b> from the client's authorized landline phone.</p>
            <p><b>This form is not a substitute for EVV</b> and may only be used for: (1) technical issues with EVV — attempt the IVR from the client's landline first (never your personal phone or the client's cell), document why both methods failed, and call the office immediately in real time — verified real-time reports do not count against your compliance rate; or (2) visits outside the client's home — still clock in/out with the app and document the off-site details here.</p>
            <p><b>Client signature required (effective May 1):</b> the client or the authorized individual on file must sign. When you call in, the office tells you who should sign. If that person isn't present, the office follows up — this may delay payment of the visit.</p>
            <p><b>A visit is approved for payment only with all of:</b> clock-in and clock-out punches, task codes entered at the time of service, and required signatures.</p>
          </div>
        )}
      </div>

      <div className="rounded-lg border border-stone-200 bg-white p-5 shadow-sm space-y-4">
        <div className="grid gap-4 sm:grid-cols-2">
          <div>
            <label className="mb-1 block text-xs font-medium text-stone-500">Work date *</label>
            <Input type="date" value={form.work_date} onChange={set("work_date")} max={new Date().toISOString().slice(0, 10)} className="border-stone-300 bg-white" data-testid="mp-work-date" />
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium text-stone-500">Timesheet week ending</label>
            <Input type="date" value={form.week_ending} onChange={set("week_ending")} className="border-stone-300 bg-white" data-testid="mp-week-ending" />
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium text-stone-500">Shift start</label>
            <Input type="time" value={form.shift_start} onChange={set("shift_start")} className="border-stone-300 bg-white" data-testid="mp-shift-start" />
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium text-stone-500">Shift end</label>
            <Input type="time" value={form.shift_end} onChange={set("shift_end")} className="border-stone-300 bg-white" data-testid="mp-shift-end" />
          </div>
        </div>
        {form.shift_start && form.shift_end && (
          <p className="rounded-md bg-accent/60 px-3 py-2 text-xs font-medium text-stone-600" data-testid="mp-duration">
            Shift length: {(() => {
              const [sh, sm] = form.shift_start.split(":").map(Number);
              const [eh, em] = form.shift_end.split(":").map(Number);
              const mins = (eh * 60 + em) - (sh * 60 + sm);
              return mins > 0 ? `${Math.floor(mins / 60)}h ${mins % 60 ? `${mins % 60}m` : ""}`.trim() : "check times (end is before start)";
            })()}
          </p>
        )}
        <div>
          <label className="mb-1 block text-xs font-medium text-stone-500">Client name (full) *</label>
          <Input value={form.client_name} onChange={set("client_name")} placeholder="Client's full name" className="border-stone-300 bg-white" data-testid="mp-client-name" />
        </div>
        <div>
          <label className="mb-1 block text-xs font-medium text-stone-500">Client / authorized signer name</label>
          <Input value={form.client_signer} onChange={set("client_signer")} placeholder="Who can confirm this shift (client or POA)" className="border-stone-300 bg-white" data-testid="mp-client-signer" />
        </div>
        <div>
          <label className="mb-1 block text-xs font-medium text-stone-500">Client address (or location of the visit)</label>
          <Input value={addr.street} onChange={(e) => setAddr({ ...addr, street: e.target.value })} placeholder="Street address" className="border-stone-300 bg-white" data-testid="mp-addr-street" />
          <div className="mt-2 grid gap-2 sm:grid-cols-3">
            <Input value={addr.city} onChange={(e) => setAddr({ ...addr, city: e.target.value })} placeholder="City" className="border-stone-300 bg-white" data-testid="mp-addr-city" />
            <Input value={addr.state} onChange={(e) => setAddr({ ...addr, state: e.target.value })} placeholder="State" className="border-stone-300 bg-white" data-testid="mp-addr-state" />
            <Input value={addr.zip} onChange={(e) => setAddr({ ...addr, zip: e.target.value })} placeholder="ZIP" className="border-stone-300 bg-white" data-testid="mp-addr-zip" />
          </div>
        </div>
        <div className="grid gap-4 sm:grid-cols-2">
          <div>
            <label className="mb-1 block text-xs font-medium text-stone-500">Signer phone (we'll call to verify)</label>
            <Input value={signerPhone} onChange={(e) => setSignerPhone(e.target.value)} placeholder="(000) 000-0000" className="border-stone-300 bg-white" data-testid="mp-signer-phone" />
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium text-stone-500">Signature date</label>
            <Input type="date" value={signerDate} onChange={(e) => setSignerDate(e.target.value)} className="border-stone-300 bg-white" data-testid="mp-signer-date" />
          </div>
        </div>
      </div>

      <div className="rounded-lg border border-stone-200 bg-white p-5 shadow-sm">
        <p className="text-sm font-medium text-stone-900">Why was the punch missed? <span className="text-stone-400">(select all that apply)</span></p>
        <div className="mt-2 grid gap-2 sm:grid-cols-2">
          {REASONS.map((r) => (
            <label key={r} className={`flex cursor-pointer items-center gap-2 rounded-md border px-3 py-2 text-sm transition-colors ${reasons.includes(r) ? "border-primary/50 bg-primary/5 text-stone-900" : "border-stone-200 text-stone-600 hover:bg-accent"}`} data-testid={`mp-reason-${r.replace(/[^A-Za-z0-9]+/g, "-").toLowerCase()}`}>
              <input type="checkbox" checked={reasons.includes(r)} onChange={() => toggle(reasons, setReasons, r)} />
              {r}
            </label>
          ))}
        </div>
        <div className="mt-3 flex justify-end">
          <DictateButton testid="mp-reason-dictate" value={form.reason_details} onReplace={(t) => setForm((f) => ({ ...f, reason_details: t }))} onText={(t) => setForm((f) => ({ ...f, reason_details: (f.reason_details ? f.reason_details + " " : "") + t }))} />
        </div>
        <Textarea value={form.reason_details} onChange={set("reason_details")} placeholder="Details — what happened, when you called the agency, etc." className="mt-1.5 border-stone-300 bg-white text-sm" data-testid="mp-reason-details" />
      </div>

      <div className="rounded-lg border border-stone-200 bg-white p-5 shadow-sm">
        <p className="text-sm font-medium text-stone-900">Tasks performed during the shift</p>
        <div className="mt-2 grid gap-1.5 sm:grid-cols-2 lg:grid-cols-3">
          {TASKS.map(([code, label]) => (
            <label key={code} className={`flex cursor-pointer items-center gap-2 rounded-md border px-2.5 py-1.5 text-xs transition-colors ${tasks.some((t) => t[0] === code) ? "border-primary/50 bg-primary/5 text-stone-900" : "border-stone-200 text-stone-600 hover:bg-accent"}`} data-testid={`mp-task-${code}`}>
              <input type="checkbox" checked={tasks.some((t) => t[0] === code)} onChange={() => toggle(tasks, setTasks, [code, label])} />
              <span><span className="font-medium">{code}</span> {label}</span>
            </label>
          ))}
        </div>
        <div className="mt-3 flex justify-end">
          <DictateButton testid="mp-notes-dictate" value={form.notes} onReplace={(t) => setForm((f) => ({ ...f, notes: t }))} onText={(t) => setForm((f) => ({ ...f, notes: (f.notes ? f.notes + " " : "") + t }))} />
        </div>
        <Textarea value={form.notes} onChange={set("notes")} placeholder="Notes about the visit (optional)" className="mt-1.5 border-stone-300 bg-white text-sm" data-testid="mp-notes" />
      </div>

      <div className="rounded-lg border border-stone-200 bg-white p-5 shadow-sm" data-testid="mp-client-sig-card">
        <p className="text-sm font-medium text-stone-900">✍️ Client signature requirement</p>
        <div className="mt-1.5 space-y-1.5 text-xs leading-relaxed text-stone-600">
          <p><b>Effective May 1st, a client signature is required on all Missed Punch Forms.</b></p>
          <p>✅ The signature must be provided by the authorized individual on file — sometimes that's the client, other times it's a representative.</p>
          <p>📞 When you call to report the missed punch, we'll let you know who should sign.</p>
          <p>⚠️ This timesheet is not valid and payable without an authorized signature below. Incomplete timesheets may delay payment of the visit. All signatures will be verified by the office.</p>
        </div>
        <div className="mt-3"><SignaturePad onChange={setClientSig} /></div>
      </div>

      <div className="rounded-lg border border-stone-200 bg-white p-5 shadow-sm space-y-3">
        <label className="flex cursor-pointer items-start gap-2 text-sm text-stone-700" data-testid="mp-certify-label">
          <input type="checkbox" checked={certified} onChange={(e) => setCertified(e.target.checked)} className="mt-1" data-testid="mp-certify" />
          <span>Certification: I certify that the information entered is true and correct. I understand that falsification or misrepresentation of this timesheet is Medicaid Fraud and it is a CRIME.</span>
        </label>
        <div>
          <label className="mb-1 block text-xs font-medium text-stone-500">Type your full legal name to sign *</label>
          <Input value={form.employee_signature} onChange={set("employee_signature")} placeholder={user?.name || "Your full name"} className="border-stone-300 bg-white font-medium" data-testid="mp-signature" />
        </div>
      </div>

      <Button onClick={submit} disabled={busy || !form.work_date || !form.client_name.trim() || !certified || !form.employee_signature.trim()}
        className="w-full bg-primary text-primary-foreground hover:bg-stone-900" data-testid="mp-submit">
        {busy ? "Submitting…" : "Submit missed punch form"}
      </Button>
    </div>
  );
}
